import React from 'react';
import {
  C,
  VIDEO_SETTINGS_W,
  VIDEO_SETTINGS_H,
  nx,
  ny,
} from '../flowCanvas.constants';

const HEADER_H = 40;
const PAD = 12;
const LABEL_X = 14;
const CONTROL_X = 110;
const ROW_H = 38;

function TogglePill({ x, y, active, onClick }) {
  const w = VIDEO_SETTINGS_W - CONTROL_X - PAD;
  return (
    <g
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{ cursor: 'pointer' }}
    >
      <rect
        x={x} y={y} width={w} height={22} rx="11"
        fill={active ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.05)'}
        stroke={active ? 'rgba(74,222,128,0.58)' : 'rgba(145,70,255,0.22)'}
        strokeWidth="1"
      />
      <circle
        cx={x + (active ? w - 11 : 11)} cy={y + 11} r="7"
        fill={active ? C.greenText : C.textMuted}
      />
      <text
        x={x + w / 2} y={y + 14}
        textAnchor="middle" fontSize="9" fontWeight="700"
        fill={active ? C.greenText : C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {active ? 'Enabled' : 'Disabled'}
      </text>
    </g>
  );
}

export default function VideoSettingsNode({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  liveFeatureData,
  onFieldChange,
}) {
  const x = overrideX ?? nx(step);
  const y = overrideY ?? ny(step, centreY);
  const left = x - VIDEO_SETTINGS_W / 2;
  const top = y - VIDEO_SETTINGS_H / 2;

  // Read from customisation vars first, fall back to feature vars
  const controlsTab = liveCustomisationData?.controls?.controls || liveFeatureData?.controls || {};
  const volume = typeof controlsTab.volume === 'number' ? controlsTab.volume : 100;
  const loopEnabled = controlsTab.loop === true;
  const isInfinite = typeof controlsTab.numberOfLoops === 'number'
    ? controlsTab.numberOfLoops === -1
    : true;
  const numLoops = (!isInfinite && typeof controlsTab.numberOfLoops === 'number')
    ? controlsTab.numberOfLoops
    : 1;

  return (
    <g
      className="fc-node fc-node--video-settings"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      {/* Shadow */}
      <rect x={left + 2} y={top + 4} width={VIDEO_SETTINGS_W} height={VIDEO_SETTINGS_H} rx="10" fill="#000" fillOpacity="0.3" />
      {/* Card */}
      <rect
        x={left} y={top} width={VIDEO_SETTINGS_W} height={VIDEO_SETTINGS_H} rx="10"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.purple}
        strokeWidth={selected ? 2 : 1.5}
      />
      {/* Header divider */}
      <line x1={left + 1} y1={top + HEADER_H} x2={left + VIDEO_SETTINGS_W - 1} y2={top + HEADER_H} stroke={C.border} strokeWidth="1" />

      {/* Header title */}
      <text x={left + 14} y={top + 24} fontSize="11.5" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {step.stepTitle || 'Video Settings'}
      </text>
      <text x={left + VIDEO_SETTINGS_W - 14} y={top + 24} textAnchor="end" fontSize="8.5" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Playback Controls
      </text>

      {/* ── Row 1: Volume ── */}
      <line x1={left + 10} y1={top + HEADER_H + ROW_H * 0} x2={left + VIDEO_SETTINGS_W - 10} y2={top + HEADER_H + ROW_H * 0} stroke={C.border} strokeWidth="1" />
      <text x={left + LABEL_X} y={top + HEADER_H + ROW_H * 0 + 16} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Volume
      </text>
      <text x={left + LABEL_X} y={top + HEADER_H + ROW_H * 0 + 30} fontSize="8" fill={C.greenText} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {volume}%
      </text>
      <foreignObject x={left + CONTROL_X} y={top + HEADER_H + ROW_H * 0 + 8} width={VIDEO_SETTINGS_W - CONTROL_X - PAD} height="22">
        <input
          type="range" min="0" max="100" value={volume}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { e.stopPropagation(); onFieldChange?.('volume', parseInt(e.target.value, 10)); }}
          style={{ width: '100%', cursor: 'pointer', accentColor: '#4ade80' }}
        />
      </foreignObject>

      {/* ── Row 2: Loop toggle ── */}
      <line x1={left + 10} y1={top + HEADER_H + ROW_H * 1} x2={left + VIDEO_SETTINGS_W - 10} y2={top + HEADER_H + ROW_H * 1} stroke={C.border} strokeWidth="1" />
      <text x={left + LABEL_X} y={top + HEADER_H + ROW_H * 1 + 23} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Loop
      </text>
      <TogglePill
        x={left + CONTROL_X}
        y={top + HEADER_H + ROW_H * 1 + 8}
        active={loopEnabled}
        onClick={() => {
          const newLoop = !loopEnabled;
          onFieldChange?.('loop', newLoop);
          if (!newLoop) onFieldChange?.('numberOfLoops', -1);
        }}
      />

      {/* ── Row 3: Infinite loop (only when loop is on) ── */}
      {loopEnabled && (
        <>
          <line x1={left + 10} y1={top + HEADER_H + ROW_H * 2} x2={left + VIDEO_SETTINGS_W - 10} y2={top + HEADER_H + ROW_H * 2} stroke={C.border} strokeWidth="1" />
          <text x={left + LABEL_X} y={top + HEADER_H + ROW_H * 2 + 23} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            Infinite
          </text>
          <TogglePill
            x={left + CONTROL_X}
            y={top + HEADER_H + ROW_H * 2 + 8}
            active={isInfinite}
            onClick={() => {
              onFieldChange?.('numberOfLoops', isInfinite ? 1 : -1);
            }}
          />
        </>
      )}

      {/* ── Row 4: Loop count (only when loop is on and not infinite) ── */}
      {loopEnabled && !isInfinite && (
        <>
          <line x1={left + 10} y1={top + HEADER_H + ROW_H * 3} x2={left + VIDEO_SETTINGS_W - 10} y2={top + HEADER_H + ROW_H * 3} stroke={C.border} strokeWidth="1" />
          <text x={left + LABEL_X} y={top + HEADER_H + ROW_H * 3 + 23} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            Loop Count
          </text>
          <foreignObject x={left + CONTROL_X} y={top + HEADER_H + ROW_H * 3 + 9} width={VIDEO_SETTINGS_W - CONTROL_X - PAD} height="22">
            <input
              type="number" min="1" value={numLoops}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1) onFieldChange?.('numberOfLoops', val);
              }}
              style={{
                width: '100%', background: 'var(--fc-circle-bg)',
                border: '1px solid rgba(145,70,255,0.35)', borderRadius: '6px',
                color: 'var(--fc-text)', fontSize: '9px', padding: '2px 6px',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </foreignObject>
        </>
      )}
    </g>
  );
}